import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

const STORAGE_KEY = 'stavyn-sidebar-collapsed';
const LG_MQ = '(min-width: 1024px)';
const TABLET_MQ = '(min-width: 768px) and (max-width: 1023px)';

const SidebarContext = createContext(null);

function readStoredCollapsed() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'true') return true;
    if (stored === 'false') return false;
  } catch {
    /* ignore */
  }
  return null;
}

function defaultCollapsed() {
  const stored = readStoredCollapsed();
  if (stored !== null) return stored;
  if (typeof window !== 'undefined' && window.matchMedia(TABLET_MQ).matches) {
    return true;
  }
  return false;
}

export function SidebarProvider({ children }) {
  const [collapsed, setCollapsedState] = useState(defaultCollapsed);
  const [isLg, setIsLg] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(LG_MQ).matches
  );

  useEffect(() => {
    const mq = window.matchMedia(LG_MQ);
    const onChange = () => setIsLg(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const persist = useCallback((next) => {
    try {
      localStorage.setItem(STORAGE_KEY, String(next));
    } catch {
      /* ignore */
    }
  }, []);

  const setCollapsed = useCallback(
    (value) => {
      const next = typeof value === 'function' ? value(collapsed) : value;
      setCollapsedState(next);
      persist(next);
    },
    [collapsed, persist]
  );

  const toggleSidebar = useCallback(() => {
    setCollapsed((prev) => !prev);
  }, [setCollapsed]);

  const effectiveCollapsed = isLg && collapsed;

  const value = useMemo(
    () => ({
      collapsed,
      effectiveCollapsed,
      isLg,
      toggleSidebar,
      setCollapsed,
    }),
    [collapsed, effectiveCollapsed, isLg, toggleSidebar, setCollapsed]
  );

  return (
    <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>
  );
}

export function useSidebar() {
  const ctx = useContext(SidebarContext);
  if (!ctx) {
    throw new Error('useSidebar must be used within SidebarProvider');
  }
  return ctx;
}
